/**
 * Header Component
 * Displays logo, vendor info, and logout button
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, User } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '@/hooks/redux';
import { logout } from '@/store/slices/authSlice';
import { Button } from '@/components/common';
import toast from 'react-hot-toast';

const Header: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isAuthenticated, email } = useAppSelector((state) => state.auth);
  const { referenceId } = useAppSelector((state) => state.vendor);

  const handleLogout = () => {
    dispatch(logout());
    toast.success('Logged out successfully');
    navigate('/login');
  };

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <h1 className="text-2xl font-bold text-primary-600">
                Loan<span className="text-primary-800">Lens</span>
              </h1>
            </div>
            <div className="hidden md:block ml-4">
              <span className="text-sm text-gray-500">Vendor Onboarding</span>
            </div>
          </div>

          {/* User Info & Actions */}
          {isAuthenticated && (
            <div className="flex items-center space-x-4">
              {/* Reference ID */}
              {referenceId && (
                <div className="hidden sm:flex items-center space-x-2 px-3 py-1.5 bg-gray-100 rounded-lg">
                  <span className="text-xs text-gray-500 font-medium">Ref ID:</span>
                  <span className="text-sm font-semibold text-gray-700">{referenceId}</span>
                </div>
              )}

              {/* User Email */}
              <div className="hidden md:flex items-center space-x-2">
                <div className="flex items-center justify-center w-8 h-8 bg-primary-100 rounded-full">
                  <User className="w-4 h-4 text-primary-600" />
                </div>
                <span className="text-sm font-medium text-gray-700">{email}</span>
              </div>

              {/* Logout Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                className="flex items-center space-x-2"
                aria-label="Logout"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
